import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, X, ChevronDown, ArrowRight, Sparkles, Shield, Zap } from 'lucide-react';
import { get } from '../../lib/apiClient';
import { useAuth } from '../../context/AuthContext';
import { encryptParams } from '../../lib/paramsCrypto';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlanFeature {
  text: string;
  category: string | null;
  included: boolean;
}

interface Plan {
  slug: string;
  name: string;
  tagline: string;
  monthly_price: number;
  annual_price: number;
  credits_label: string;
  is_popular: boolean;
  features: PlanFeature[];
}

interface ComparisonRow {
  feature: string;
  silver: boolean | string;
  gold: boolean | string;
  platinum: boolean | string;
}

interface Faq {
  q: string;
  a: string;
}

interface PricingData {
  plans: Plan[];
  comparison: ComparisonRow[];
  faqs: Faq[];
}

// ---------------------------------------------------------------------------
// Icon + colour map — keyed by plan slug (UI concern, not stored in DB)
// ---------------------------------------------------------------------------

const PLAN_VISUALS: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  silver:   { icon: Zap,      color: 'var(--sky)',   bg: 'var(--sky-light)' },
  gold:     { icon: Sparkles, color: 'var(--amber)', bg: 'var(--amber-light)' },
  platinum: { icon: Shield,   color: '#7C3AED',      bg: '#EDE9FE' },
};

const PLAN_ORDER: Record<string, number> = { silver: 1, gold: 2, platinum: 3 };

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [searchParams] = useSearchParams();
  const paymentCancelled = searchParams.get('payment') === 'cancelled';
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const currentPlan = user?.plan ?? null;
  const currentInterval = annual ? 'yearly' : 'monthly';

  const { data: pricingResponse, isLoading } = useQuery({
    queryKey: ['pricing'],
    queryFn: () => get<PricingData>('pricing'),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  const { data: subscriptionData } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => get<{ plan: string; plan_interval: string; plan_status: string }>('subscription'),
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
  });
  const sub = subscriptionData?.data;

  const plans      = pricingResponse?.data.plans      ?? [];
  const comparisonFeatures = pricingResponse?.data.comparison ?? [];
  const faqs       = pricingResponse?.data.faqs       ?? [];

  const handleSignupNav = async (planSlug: string, action: 'trial' | 'purchase') => {
    const interval = annual ? 'yearly' : 'monthly';
    const token = await encryptParams({ plan: planSlug, action, interval });
    navigate(`/signup?token=${token}`);
  };

  return (
    <div className="pr">
      <section className="pr-hero">
        <div className="pr-hero-bg" />
        <div className="pr-hero-dots" />
        <div className="container pr-hero-inner">
          <div className="pr-hero-pill">
            <Sparkles size={14} />
            Pricing
          </div>
          <h1 className="pr-hero-title">Simple, transparent pricing</h1>
          <p className="pr-hero-desc">Choose the plan that fits your team. Start free for 14 days.</p>
          <div className="pr-toggle-wrap">
            <button className={`pr-toggle-btn ${!annual ? 'pr-toggle-active' : ''}`} onClick={() => setAnnual(false)}>
              Monthly
            </button>
            <button className={`pr-toggle-btn ${annual ? 'pr-toggle-active' : ''}`} onClick={() => setAnnual(true)}>
              Annual
              <span className="pr-save-badge">Save 20%</span>
            </button>
          </div>
        </div>
      </section>

      {paymentCancelled && (
        <div style={{ background: '#FFF8E1', borderBottom: '1px solid #FFE082', padding: '12px 24px', textAlign: 'center', fontSize: 14, color: '#F57F17' }}>
          Your payment was cancelled. Choose a plan below to complete your purchase.
        </div>
      )}

      <section className="pr-cards-section">
        <div className="container">
          <div className="pr-cards-grid">
            {isLoading && [1, 2, 3].map((i) => (
              <div key={i} className="pr-card" style={{ minHeight: 520, background: 'var(--gray-50)' }} />
            ))}
            {plans.map((plan) => {
              const visuals = PLAN_VISUALS[plan.slug] ?? { icon: Zap, color: 'var(--sky)', bg: 'var(--sky-light)' };
              const PlanIcon = visuals.icon;
              const isCurrentPlan = isAuthenticated
                && sub?.plan === plan.slug
                && (sub?.plan_interval ?? 'monthly') === currentInterval
                && ['active', 'trialing'].includes(sub?.plan_status ?? '');
              const isSamePlanDiffInterval = isAuthenticated
                && sub?.plan === plan.slug
                && (sub?.plan_interval ?? 'monthly') !== currentInterval
                && ['active', 'trialing'].includes(sub?.plan_status ?? '');
              const currentPlanOrder = PLAN_ORDER[currentPlan ?? ''] ?? 0;
              const thisPlanOrder = PLAN_ORDER[plan.slug] ?? 0;
              const isUpgrade = thisPlanOrder > currentPlanOrder;
              return (
                <div key={plan.slug} className={`pr-card ${plan.is_popular && !isCurrentPlan ? 'pr-card-popular' : ''} ${isCurrentPlan ? 'pr-card-current' : ''}`}>
                  {isCurrentPlan && <div className="pr-current-badge">Your Current Plan</div>}
                  {plan.is_popular && !isCurrentPlan && <div className="pr-popular-badge">Most Popular</div>}
                  <div className="pr-card-header">
                    <div className="pr-plan-icon" style={{ background: visuals.bg, color: visuals.color }}>
                      <PlanIcon size={20} />
                    </div>
                    <h3 className="pr-plan-name">{plan.name}</h3>
                    <p className="pr-plan-tagline">{plan.tagline}</p>
                  </div>
                  <div className="pr-price-block">
                    <span className="pr-price">${annual ? plan.annual_price : plan.monthly_price}</span>
                    <span className="pr-price-period">/mo</span>
                  </div>
                  {annual && (
                    <p className="pr-price-original">${plan.monthly_price}/mo</p>
                  )}
                  <p className="pr-credits">{plan.credits_label}</p>
                  <div className="pr-plan-btns">
                    {isCurrentPlan ? (
                      <button className="pr-plan-btn pr-plan-btn-current" disabled>
                        ✓ Current Plan
                      </button>
                    ) : isAuthenticated ? (
                      <button
                        onClick={() => navigate('/app/upgrade-plan')}
                        className={`pr-plan-btn ${isSamePlanDiffInterval ? 'pr-plan-btn-outline' : isUpgrade ? (plan.is_popular ? 'pr-plan-btn-primary' : '') : 'pr-plan-btn-outline pr-plan-btn-downgrade'}`}
                      >
                        {isSamePlanDiffInterval
                          ? `Switch to ${currentInterval === 'yearly' ? 'Yearly' : 'Monthly'}`
                          : isUpgrade
                            ? `Upgrade to ${plan.name}`
                            : `Downgrade to ${plan.name}`}
                      </button>
                    ) : (
                      <>
                        <button
                          className={`pr-plan-btn ${plan.is_popular ? 'pr-plan-btn-primary' : ''}`}
                          onClick={() => handleSignupNav(plan.slug, 'trial')}
                        >
                          Start Free Trial
                        </button>
                        <button
                          className="pr-plan-btn pr-plan-btn-outline"
                          onClick={() => handleSignupNav(plan.slug, 'purchase')}
                        >
                          Buy Now
                        </button>
                      </>
                    )}
                  </div>
                  <div className="pr-features-list">
                    {(() => {
                      // Group features by category, preserving insertion order
                      const groups: { category: string | null; items: PlanFeature[] }[] = [];
                      const seen = new Map<string | null, PlanFeature[]>();
                      for (const f of plan.features) {
                        const key = f.category ?? null;
                        if (!seen.has(key)) {
                          const items: PlanFeature[] = [];
                          seen.set(key, items);
                          groups.push({ category: key, items });
                        }
                        seen.get(key)!.push(f);
                      }
                      return groups.map((group, gi) => (
                        <div key={group.category ?? `__ungrouped_${gi}`}>
                          {group.category && (
                            <div className={`pr-feature-category${gi === 0 ? ' pr-feature-category-first' : ''}`}>
                              {group.category}
                            </div>
                          )}
                          {group.items.map((f) => (
                            <div key={f.text} className={`pr-feature-item ${!f.included ? 'pr-feature-disabled' : ''}`}>
                              {f.included ? (
                                <Check size={16} color="var(--green)" style={{ flexShrink: 0 }} />
                              ) : (
                                <X size={16} color="var(--gray-200)" style={{ flexShrink: 0 }} />
                              )}
                              <span>{f.text}</span>
                            </div>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pr-comparison-section">
        <div className="container">
          <h2 className="pr-section-title">Full feature comparison</h2>
          <div className="pr-table-wrap">
            <table className="pr-table">
              <thead>
                <tr>
                  <th className="pr-th-feature">Feature</th>
                  {plans.map((plan) => (
                    <th key={plan.slug} className={`pr-th-plan ${plan.is_popular ? 'pr-th-highlight' : ''}`}>
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((row) => (
                  <tr key={row.feature}>
                    <td className="pr-td-feature">{row.feature}</td>
                    {plans.map((plan) => {
                      const val = row[plan.slug as keyof ComparisonRow];
                      return (
                        <td key={plan.slug} className={`pr-td-value ${plan.is_popular ? 'pr-td-highlight' : ''}`}>
                          {typeof val === 'boolean' ? (
                            val ? <Check size={16} color="var(--green)" /> : <X size={16} color="var(--gray-200)" />
                          ) : (
                            <span>{val as string}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="pr-faq-section">
        <div className="container pr-faq-container">
          <h2 className="pr-section-title">Frequently asked questions</h2>
          <div className="pr-faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className="pr-faq-item">
                <button className="pr-faq-btn" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{faq.q}</span>
                  <ChevronDown size={20} className={`pr-faq-chevron ${openFaq === i ? 'pr-faq-chevron-open' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="pr-faq-answer">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pr-cta-section">
        <div className="pr-cta-glow" />
        <div className="container pr-cta-inner">
          {isAuthenticated ? (
            <>
              <h2 className="pr-cta-title">Ready to grow your pipeline?</h2>
              <p className="pr-cta-desc">Go to your dashboard and start finding leads.</p>
              <Link to="/app/dashboard" className="pr-cta-btn">
                Go to Dashboard <ArrowRight size={18} />
              </Link>
            </>
          ) : (
            <>
              <h2 className="pr-cta-title">Start your free trial today</h2>
              <p className="pr-cta-desc">14 days free. No credit card required. Cancel anytime.</p>
              <Link to="/signup" className="pr-cta-btn">
                Get Started Free <ArrowRight size={18} />
              </Link>
            </>
          )}
        </div>
      </section>

      <style>{`
        .pr { overflow-x: hidden; }

        .pr-hero {
          background: var(--navy); position: relative;
          padding: 140px 0 80px; text-align: center; overflow: hidden;
        }
        .pr-hero-bg {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(77,184,232,0.18), transparent 70%);
        }
        .pr-hero-dots {
          position: absolute; inset: 0; opacity: 0.03;
          background-image: radial-gradient(circle, #fff 1px, transparent 1px);
          background-size: 32px 32px;
        }
        .pr-hero-inner { position: relative; z-index: 1; }
        .pr-hero-pill {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(77,184,232,0.12); border: 1px solid rgba(77,184,232,0.25);
          border-radius: 100px; padding: 6px 18px; margin-bottom: 24px;
          font-size: 13px; font-weight: 600; color: var(--sky);
        }
        .pr-hero-title {
          font-size: 52px; font-weight: 800; color: #fff;
          line-height: 1.1; margin: 0 auto 20px; letter-spacing: -0.03em;
        }
        .pr-hero-desc {
          font-size: 19px; color: rgba(255,255,255,0.6);
          max-width: 560px; margin: 0 auto 36px; line-height: 1.7;
        }

        .pr-toggle-wrap {
          display: inline-flex; align-items: center; gap: 0;
          background: rgba(255,255,255,0.1); border-radius: 999px; padding: 4px;
        }
        .pr-toggle-btn {
          padding: 10px 24px; border-radius: 999px; font-size: 15px;
          font-weight: 600; background: transparent; color: rgba(255,255,255,0.7);
          border: none; cursor: pointer; display: flex; align-items: center; gap: 8px;
          transition: all 0.2s;
        }
        .pr-toggle-active { background: #fff; color: var(--navy); }
        .pr-save-badge {
          background: var(--green); color: #fff; font-size: 11px;
          font-weight: 700; padding: 2px 8px; border-radius: 999px;
        }

        .pr-cards-section { padding: 80px 0; background: #fff; }
        .pr-cards-grid {
          display: grid; grid-template-columns: repeat(3, 1fr);
          gap: 24px; align-items: start;
        }

        .pr-card {
          background: #fff; border-radius: 20px; padding: 36px 28px;
          border: 1px solid var(--gray-200); position: relative;
          transition: all 0.3s;
        }
        .pr-card:hover { box-shadow: 0 12px 40px rgba(0,0,0,0.08); transform: translateY(-4px); }
        .pr-card-popular {
          border: 2px solid var(--sky);
          box-shadow: 0 12px 40px rgba(77,184,232,0.12);
        }
        .pr-card-popular:hover { box-shadow: 0 16px 48px rgba(77,184,232,0.18); }
        .pr-popular-badge {
          position: absolute; top: -14px; left: 50%; transform: translateX(-50%);
          background: var(--sky); color: #fff; font-size: 12px; font-weight: 700;
          padding: 5px 18px; border-radius: 999px; white-space: nowrap;
        }
        .pr-card-current {
          border: 2px solid var(--green);
          box-shadow: 0 12px 40px rgba(58,170,53,0.12);
        }
        .pr-card-current:hover { box-shadow: 0 16px 48px rgba(58,170,53,0.18); }
        .pr-current-badge {
          position: absolute; top: -14px; left: 50%; transform: translateX(-50%);
          background: var(--green); color: #fff; font-size: 12px; font-weight: 700;
          padding: 5px 18px; border-radius: 999px; white-space: nowrap;
        }
        .pr-plan-btn-current {
          background: var(--gray-100); color: var(--gray-500);
          border: 1px solid var(--gray-200); cursor: not-allowed; opacity: 0.8;
        }

        .pr-card-header { margin-bottom: 24px; }
        .pr-plan-icon {
          width: 48px; height: 48px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 16px;
        }
        .pr-plan-name { font-size: 22px; font-weight: 800; color: var(--navy); margin-bottom: 4px; }
        .pr-plan-tagline { font-size: 14px; color: var(--gray-500); margin: 0; }

        .pr-price-block { margin-bottom: 4px; }
        .pr-price { font-size: 48px; font-weight: 800; color: var(--navy); letter-spacing: -0.03em; }
        .pr-price-period { font-size: 16px; color: var(--gray-500); font-weight: 500; }
        .pr-price-original {
          font-size: 13px; color: var(--gray-500); text-decoration: line-through;
          margin: 0 0 4px;
        }
        .pr-credits {
          font-size: 14px; color: var(--sky); font-weight: 600; margin: 0 0 24px;
        }

        .pr-plan-btn {
          display: block; width: 100%; padding: 12px 20px;
          border-radius: 10px; font-size: 15px; font-weight: 600;
          text-align: center; text-decoration: none; margin-bottom: 24px;
          background: var(--gray-50); color: var(--navy);
          border: 1px solid var(--gray-200); transition: all 0.2s;
        }
        .pr-plan-btn:hover { text-decoration: none; background: var(--gray-100); }
        .pr-plan-btn-primary {
          background: var(--green); color: #fff; border-color: var(--green);
          box-shadow: 0 4px 14px rgba(58,170,53,0.3);
        }
        .pr-plan-btn-primary:hover { background: #2e9229; }
        .pr-plan-btns { display: flex; flex-direction: column; gap: 8px; margin-bottom: 24px; }
        .pr-plan-btns .pr-plan-btn { margin-bottom: 0; }
        .pr-plan-btn-outline {
          background: transparent; color: var(--navy);
          border: 1px solid var(--gray-300);
        }
        .pr-plan-btn-outline:hover { background: var(--gray-50); text-decoration: none; }
        .pr-plan-btn-downgrade { color: var(--gray-500); }

        .pr-features-list { }
        .pr-feature-category {
          font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
          color: var(--gray-400); text-transform: uppercase;
          padding: 12px 0 4px;
        }
        .pr-feature-category-first { padding-top: 0; }
        .pr-feature-item {
          display: flex; align-items: center; gap: 10px;
          padding: 8px 0; border-top: 1px solid var(--gray-100);
          font-size: 14px; color: var(--gray-900);
        }
        .pr-feature-disabled { color: var(--gray-500); }

        .pr-comparison-section { padding: 80px 0; background: var(--gray-50); }
        .pr-section-title {
          font-size: 32px; font-weight: 800; color: var(--navy);
          text-align: center; margin-bottom: 40px; letter-spacing: -0.02em;
        }
        .pr-table-wrap {
          background: #fff; border-radius: 16px; overflow: auto;
          border: 1px solid var(--gray-200); box-shadow: 0 4px 20px rgba(0,0,0,0.04);
        }
        .pr-table { width: 100%; border-collapse: collapse; font-size: 14px; min-width: 500px; }
        .pr-table thead tr { border-bottom: 2px solid var(--gray-200); }
        .pr-th-feature {
          text-align: left; padding: 16px 20px; font-weight: 600;
          color: var(--gray-500); font-size: 13px; text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .pr-th-plan {
          text-align: center; padding: 16px 20px; font-weight: 700;
          color: var(--navy); font-size: 15px;
        }
        .pr-th-highlight { color: var(--sky); }
        .pr-table tbody tr { border-bottom: 1px solid var(--gray-100); }
        .pr-table tbody tr:last-child { border-bottom: none; }
        .pr-td-feature { padding: 14px 20px; font-weight: 500; color: var(--gray-900); }
        .pr-td-value { text-align: center; padding: 14px 20px; color: var(--gray-900); }
        .pr-td-highlight { background: rgba(77,184,232,0.03); }

        .pr-faq-section { padding: 80px 0; background: #fff; }
        .pr-faq-container { max-width: 720px; }
        .pr-faq-list { }
        .pr-faq-item { border-bottom: 1px solid var(--gray-200); }
        .pr-faq-btn {
          width: 100%; display: flex; align-items: center; justify-content: space-between;
          padding: 20px 0; background: none; border: none; cursor: pointer;
          font-size: 16px; font-weight: 600; color: var(--gray-900); text-align: left;
          gap: 16px;
        }
        .pr-faq-chevron {
          flex-shrink: 0; color: var(--gray-500);
          transition: transform 0.2s;
        }
        .pr-faq-chevron-open { transform: rotate(180deg); }
        .pr-faq-answer {
          padding: 0 0 20px; font-size: 15px; color: var(--gray-500); line-height: 1.7;
        }

        .pr-cta-section {
          background: var(--navy); padding: 80px 0;
          text-align: center; position: relative; overflow: hidden;
        }
        .pr-cta-glow {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 60% 50% at 50% 50%, rgba(77,184,232,0.12), transparent);
        }
        .pr-cta-inner { position: relative; z-index: 1; }
        .pr-cta-title {
          font-size: 38px; font-weight: 800; color: #fff;
          margin-bottom: 16px; letter-spacing: -0.02em;
        }
        .pr-cta-desc {
          font-size: 18px; color: rgba(255,255,255,0.6);
          max-width: 500px; margin: 0 auto 32px;
        }
        .pr-cta-btn {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--green); color: #fff;
          font-size: 16px; font-weight: 700; padding: 14px 32px;
          border-radius: 10px; text-decoration: none;
          box-shadow: 0 4px 14px rgba(58,170,53,0.4); transition: all 0.2s;
        }
        .pr-cta-btn:hover { text-decoration: none; opacity: 0.9; }

        /* ========== TABLET (768px) ========== */
        @media (max-width: 768px) {
          .pr-hero { padding: 120px 0 60px; }
          .pr-hero-title { font-size: 36px; }
          .pr-hero-desc { font-size: 16px; margin: 0 auto 28px; }

          .pr-cards-section { padding: 56px 0; }
          .pr-cards-grid { grid-template-columns: 1fr; max-width: 420px; margin: 0 auto; gap: 32px; }
          .pr-card { padding: 32px 24px; }

          .pr-comparison-section { padding: 56px 0; }
          .pr-section-title { font-size: 26px; margin-bottom: 28px; }

          .pr-faq-section { padding: 56px 0; }
          .pr-faq-btn { font-size: 15px; }

          .pr-cta-title { font-size: 28px; }
          .pr-cta-desc { font-size: 16px; }
        }

        /* ========== SMALL MOBILE (480px) ========== */
        @media (max-width: 480px) {
          .pr-hero { padding: 110px 0 48px; }
          .pr-hero-title { font-size: 28px; }
          .pr-hero-desc { font-size: 15px; }

          .pr-toggle-btn { padding: 8px 16px; font-size: 14px; }
          .pr-save-badge { font-size: 10px; padding: 2px 6px; }

          .pr-cards-section { padding: 40px 0; }
          .pr-cards-grid { max-width: 100%; gap: 28px; }
          .pr-card { padding: 28px 20px; border-radius: 16px; }
          .pr-price { font-size: 40px; }
          .pr-plan-name { font-size: 20px; }

          .pr-comparison-section { padding: 40px 0; }
          .pr-section-title { font-size: 22px; }
          .pr-table { font-size: 13px; }
          .pr-th-feature, .pr-td-feature { padding: 10px 12px; }
          .pr-th-plan, .pr-td-value { padding: 10px 8px; }

          .pr-faq-section { padding: 40px 0; }
          .pr-faq-btn { font-size: 14px; padding: 16px 0; }
          .pr-faq-answer { font-size: 14px; }

          .pr-cta-section { padding: 56px 0; }
          .pr-cta-title { font-size: 24px; }
          .pr-cta-desc { font-size: 15px; }
          .pr-cta-btn { padding: 12px 24px; font-size: 15px; }
        }
      `}</style>
    </div>
  );
}
